import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { dateTime } from '@guestgoodie/helpers'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete02Icon, SubtitleIcon } from '@hugeicons/core-solid-rounded'
import * as Popover from '@radix-ui/react-popover'
import { Trans, useLingui } from '@lingui/react/macro'

import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { popoverContainer } from '@/utils/popovers'

interface Props {
  captions?: GraphQL.Caption[] | null
  src: string
  locale: GraphQL.AppLocale
  onChange: (captions: GraphQL.Caption[]) => void
}

function Caption({
  value,
  max,
  onChange,
  onDelete
}: {
  value: GraphQL.Caption
  max?: string
  onChange: (value: GraphQL.Caption) => void
  onDelete: () => void
}) {
  const { t } = useLingui()

  const [startAt, setStartAt] = useState(
    dateTime.formatSecondsToTime(value.startAt)
  )

  const [endAt, setEndAt] = useState(dateTime.formatSecondsToTime(value.endAt))
  const [text, setText] = useState(value.text)

  useEffect(() => {
    const newStart = dateTime.timeToSeconds(startAt)
    const newEnd = dateTime.timeToSeconds(endAt)

    if (
      value.startAt === newStart &&
      value.endAt === newEnd &&
      text === value.text
    )
      return

    onChange({
      ...value,
      startAt: newStart,
      endAt: newEnd,
      text
    })
  }, [value, startAt, endAt, text, onChange])

  return (
    <div className="flex w-full flex-col gap-1 py-1">
      <div className="flex w-full flex-row items-center justify-between gap-1">
        <div className="flex flex-row items-center gap-2">
          <Input
            type="time"
            value={startAt}
            onChange={event => setStartAt(event.target.value)}
            min="00:00"
            max={max}
          />
          <Input
            type="time"
            value={endAt}
            onChange={event => setEndAt(event.target.value)}
            min={startAt}
            max={max}
          />
        </div>
        <Button onClick={onDelete} Icon={Delete02Icon} />
      </div>
      <Input
        type="text"
        value={text}
        placeholder={t`Subtitle text`}
        onChange={event => setText(event.target.value)}
      />
    </div>
  )
}

function AddCaptions({ src, captions: caps, onChange, locale }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [max, setMax] = useState<string>()

  const captions = useMemo(() => caps ?? [], [caps])

  const localized = useMemo(
    () => captions.filter(c => c.locale === locale),
    [captions, locale]
  )

  useEffect(() => {
    const videoElement = videoRef.current

    if (!videoElement) return

    const handleLoadedMetadata = () => {
      setMax(dateTime.formatSecondsToTime(videoElement.duration))
    }

    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata)

    // If the video is already loaded, get duration immediately
    if (videoElement.readyState >= 2) handleLoadedMetadata()

    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [])

  const onUpdate = useCallback(
    (index: number, caption: GraphQL.Caption) => {
      const newCaptions = captions.map(function (cap, i) {
        if (i === index) return caption
        return cap
      })

      onChange(newCaptions)
    },
    [captions, onChange]
  )

  const onAdd = useCallback(() => {
    const lastCaption = localized[captions.length - 1]
    const defaultTime = lastCaption?.endAt ?? 0
    const endTime = videoRef.current?.duration ?? defaultTime

    const newCaptions = [
      ...captions,
      {
        startAt: defaultTime,
        endAt: isNaN(endTime) ? defaultTime : endTime,
        text: '',
        locale
      }
    ]

    onChange(newCaptions)
  }, [captions, onChange, locale, localized])

  const onDelete = useCallback(
    (index: number) => {
      const newCaptions = captions.toSpliced(index, 1)

      onChange(newCaptions)
    },
    [captions, onChange]
  )

  useEffect(() => {
    if (!videoRef.current) return

    const track =
      videoRef.current.textTracks[0] ??
      videoRef.current.addTextTrack('captions')

    Array.from(track.cues ?? []).forEach(cue => track.removeCue(cue))

    localized.forEach(function (caption) {
      const cue = new VTTCue(caption.startAt, caption.endAt, caption.text)

      track.addCue(cue)
    })

    track.mode = 'showing'
  }, [localized])

  return (
    <div className="flex h-full max-h-(--radix-popover-content-available-height) w-[500px] flex-col gap-4 pt-5">
      <video src={src} loop controls ref={videoRef} className="hidden h-0" />
      <div
        className="flex w-full flex-col gap-4 overflow-y-auto px-5"
        // This is needed because of radix Popover, see: https://github.com/radix-ui/primitives/issues/1159
        onWheel={event => event.stopPropagation()}
        onScroll={event => event.stopPropagation()}
        onTouchMove={event => event.stopPropagation()}
      >
        {captions.map(function (caption, index) {
          if (caption.locale !== locale) return null

          return (
            <Caption
              key={index}
              max={max}
              value={caption}
              onDelete={() => onDelete(index)}
              onChange={cap => onUpdate(index, cap)}
            />
          )
        })}
      </div>
      <div className="flex h-[80px] w-full flex-row p-5">
        <Button onClick={onAdd} variant="outline">
          <Trans>Add new Subtitle</Trans>
        </Button>
      </div>
    </div>
  )
}

export default function AddCaptionsPopover(props: Props) {
  const { t } = useLingui()

  return (
    <Popover.Root>
      <Popover.Trigger
        className="rounded bg-black/50 p-1 text-white outline-primary hover:bg-black/70"
        aria-label={t`Edit Captions`}
      >
        <HugeiconsIcon icon={SubtitleIcon} className="size-full" />
      </Popover.Trigger>
      <Popover.Portal container={popoverContainer}>
        <Popover.Content
          side="top"
          collisionPadding={20}
          sideOffset={5}
          className="relative overflow-hidden rounded bg-white shadow-xl z-popover"
        >
          <AddCaptions {...props} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
